import { FlowGrid, Row, Separator, useContainerWidth, colors, Overline, Subheading, H2, H4 } from '@fs/zion-ui'
import { useTranslation, Trans } from 'react-i18next'
import React from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { PillButton } from '../buttons'
import { circlesFireJpg, circlesSeafoamJpg } from '../../images'
import { cventConfig, FLAGS, registrationConfig } from '../../config'
import { BorderCard } from '../card'
import { conferenceConfig, day } from '../../config/time'
import { useTimeTravel } from '../../providers/QualityAssuranceProviders'

const { attendeePricing, pricing, pricingDetails: pricingDetailsConfig } = registrationConfig

const getConferenceDayNumber = (currentTime) => {
  const currentYear = conferenceConfig.year
  const conferenceStart = conferenceConfig[currentYear]?.conference?.starts

  if (!conferenceStart) {
    return 0
  }

  const startDate = new Date(conferenceStart)
  const currentDate = new Date(currentTime)

  // Reset time to midnight for accurate day comparison
  startDate.setHours(0, 0, 0, 0)
  currentDate.setHours(0, 0, 0, 0)

  const diffInMs = currentDate - startDate
  const diffInDays = Math.floor(diffInMs / day)

  if (diffInDays < 0) {
    return 0
  }

  return diffInDays + 1
}

const PricingCard = ({ title, overline, heading, subHeading, to, linkName, src, flavor, buttonText }) => {
  return (
    <BorderCard backgroundImage={src} topHeading={title} flavor={flavor} hideDivider as="h3">
      <Separator />
      <H4 size="H2" centered>
        <Overline>{overline}</Overline>
        {heading}
        <Separator size="xxs" />
        <Subheading>{subHeading}</Subheading>
      </H4>
      <Separator size="sm" />
      <Row alignX="center">
        <PillButton to={to} linkName={linkName} target="_blank" noPrependLocaleCode>
          {buttonText}
        </PillButton>
      </Row>
      <Separator />
    </BorderCard>
  )
}

export default function PricingCards() {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const pricingFlag = useFeatureFlag(FLAGS.EARLY_BIRD_PRICING)
  const { early, standard, lastChance, door } = attendeePricing
  const { getNow } = useTimeTravel()

  // Get current treatment, default to 'standard' if invalid
  const validTreatments = Object.values(pricing)
  const treatment = validTreatments.includes(pricingFlag?.treatment) ? pricingFlag.treatment : pricing.STANDARD

  // Map treatment to pricing amounts
  const pricesByTreatment = {
    [pricing.EARLY]: early,
    [pricing.LAST_CHANCE]: lastChance,
    [pricing.STANDARD]: standard,
    [pricing.DOOR]: door,
  }

  const config = {
    ...pricingDetailsConfig[treatment],
    prices: pricesByTreatment[treatment],
  }

  const conferenceDayNumber = getConferenceDayNumber(getNow())
  const shouldShow3DayPass = conferenceDayNumber <= 2

  return (
    <>
      <H2 size="H3" centered>
        {t('in-person.pricing.2024', '{year} In-Person Pricing', { year: conferenceConfig.year })}
        <Separator size="xxs" />
        <Subheading>
          {t(
            'in-person.pricing.subheading',
            'Come to Salt Lake City to experience 250+ exclusive sessions and develop new friendships and reunite with old friends.'
          )}
        </Subheading>
      </H2>
      <Separator />
      <FlowGrid columnCount={atWidth({ default: 1, md: shouldShow3DayPass ? 2 : 1 })}>
        {shouldShow3DayPass && (
          <PricingCard
            src={circlesFireJpg}
            title={t('in-person.pricing.3-day-pass', '3-Day Pass')}
            overline={
              config.overline.threeDay ? t(config.overline.threeDay.key, config.overline.threeDay.default) : '\u00A0'
            }
            heading={
              <Trans
                i18nKey="in-person.pricing.price"
                // eslint-disable-next-line no-template-curly-in-string
                defaults="${price} <S>US</S>"
                values={{ price: config.prices.threeDay }}
                components={{ S: <span style={{ color: colors.gray50 }} /> }}
              />
            }
            subHeading={
              config.subheading.threeDay && (
                <Trans
                  i18nKey={config.subheading.threeDay.key}
                  defaults={config.subheading.threeDay.default}
                  components={{ S: <s /> }}
                />
              )
            }
            to={cventConfig.registrationUrl}
            linkName="Registration: 3-day-pass"
            flavor="mango"
            buttonText={t('in-person.pricing.get-3-day-pass', 'Get 3-Day Pass')}
          />
        )}
        <PricingCard
          src={circlesSeafoamJpg}
          title={t('in-person.pricing.1-day-pass', '1-Day Pass')}
          overline={config.overline.oneDay ? t(config.overline.oneDay.key, config.overline.oneDay.default) : '\u00A0'}
          heading={
            <Trans
              i18nKey="in-person.pricing.price"
              // eslint-disable-next-line no-template-curly-in-string
              defaults="${price} <S>US</S>"
              values={{ price: config.prices.oneDay }}
              components={{ S: <span style={{ color: colors.gray50 }} /> }}
            />
          }
          subHeading={
            config.subheading.oneDay && (
              <Trans
                i18nKey={config.subheading.oneDay.key}
                defaults={config.subheading.oneDay.default}
                components={{ S: <s /> }}
              />
            )
          }
          to={cventConfig.registrationUrl}
          linkName="Registration: 1-day-pass"
          flavor="fire"
          buttonText={t('in-person.pricing.get-1-day-pass', 'Get 1-Day Pass')}
        />
      </FlowGrid>
    </>
  )
}
